import React, { useState } from "react";
import { GoogleLogin } from "@react-oauth/google";
import { SocialLogin } from "@capgo/capacitor-social-login";
import { Loader2 } from "lucide-react";

import { isNativeApp } from "../../lib/native";
import { showErrorToast } from "../../lib/toast";
import {
  CLIENT_ID,
  GOOGLE_ANDROID_SERVER_CLIENT_ID,
  GOOGLE_IOS_CLIENT_ID,
  GOOGLE_IOS_SERVER_CLIENT_ID,
} from "../../constants/app";

type GoogleLoginProps = React.ComponentProps<typeof GoogleLogin>;

let nativeInit: Promise<void> | null = null;

function initNativeGoogle(): Promise<void> {
  nativeInit ??= SocialLogin.initialize({
    google: {
      webClientId: GOOGLE_ANDROID_SERVER_CLIENT_ID,
      iOSClientId: GOOGLE_IOS_CLIENT_ID,
      iOSServerClientId: GOOGLE_IOS_SERVER_CLIENT_ID,
      mode: "online",
    },
  });
  return nativeInit;
}

// Google's web button (Google Identity Services) refuses to run inside an
// app WebView, so the native app signs in through the OS account picker and
// hands the resulting ID token to the same onSuccess({ credential }) callback
// the web button uses - the backend's /auth/google route doesn't change.
export function GoogleSignInButton(props: GoogleLoginProps) {
  if (!isNativeApp) return <GoogleLogin {...props} />;
  return <NativeGoogleButton onSuccess={props.onSuccess} onError={props.onError} />;
}

// What to tell the user for a failed native sign-in. "Account reauth failed" and
// "Developer console is not set up" mean Google doesn't recognise this build's
// package + signing key (see MOBILE.md, Google sign-in); "No credentials
// available" can also mean the phone has no Google account.
function googleSignInErrorMessage(message: string): string {
  if (/no credentials available/i.test(message)) {
    return "Google sign-in found no account to use. Check that a Google account is added on this phone, then try again.";
  }
  if (/network|timeout|unreachable/i.test(message)) {
    return "Google sign-in needs an internet connection. Check your connection and try again.";
  }
  if (/reauth|developer console/i.test(message)) {
    return "Google sign-in isn't available in this version of the app yet. Please sign in with your email instead.";
  }
  return "Google sign-in isn't working right now. Please try again later.";
}

function NativeGoogleButton({ onSuccess, onError }: Pick<GoogleLoginProps, "onSuccess" | "onError">) {
  const [isLoading, setIsLoading] = useState(false);

  const handleClick = async () => {
    setIsLoading(true);
    try {
      await initNativeGoogle();
      // No `scopes`: openid/email/profile are the plugin's defaults, and passing
      // any scopes on Android requires a modified MainActivity (it rejects otherwise).
      const { result } = await SocialLogin.login({ provider: "google", options: {} });
      const idToken = "idToken" in result ? result.idToken : null;
      if (!idToken) throw new Error("No ID token returned from Google");
      onSuccess({ credential: idToken, clientId: CLIENT_ID, select_by: "btn" });
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      // Full plugin diagnostics (package, signing SHA-1, client ID) stay in the
      // log for developers; users get a short message they can act on.
      console.error("Native Google sign-in failed:", message);
      // Closing the account picker isn't an error worth reporting.
      if (/cancel/i.test(message) && !/reauth/i.test(message)) return;
      showErrorToast(googleSignInErrorMessage(message));
      onError?.();
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={isLoading}
      className="flex items-center justify-center gap-3 w-full h-12 rounded-xl border border-gray-300 bg-white text-slate-800 font-medium shadow-sm active:shadow-none disabled:opacity-70"
    >
      {isLoading ? (
        <Loader2 className="w-5 h-5 animate-spin" aria-hidden="true" />
      ) : (
        <svg width="20" height="20" viewBox="0 0 48 48" aria-hidden="true">
          <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z" />
          <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z" />
          <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z" />
          <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z" />
        </svg>
      )}
      Continue with Google
    </button>
  );
}
